import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CategoryType } from "../../types";
import OpportunityFilters from "../../components/OpportunityFilters/OpportunityFilters";
import OpportunityTable from "../../components/OpportunityTable/OpportunityTable";
import "./opportunityDiscovery.scss";

interface OpportunityDiscoveryProps {
  type?: CategoryType;
  disableQuerySync?: boolean;
}

/**
 * Carbonized layout (Task 4, Opportunity Discovery + AI Profiling flow): the Tailwind
 * `grid-cols-6` two-pane layout is replaced by `opportunity-discovery.scss`'s own CSS grid (same
 * 1:2 filters/table ratio), and the hosted `OpportunityTable` is now Carbon `DataTable`-backed.
 * Everything else — the `type` control contract, `categoryFilters` wiring passed down to both
 * children, and the 0-indexed `page`/fixed `size=10` query params driving `useOpportunities` — is
 * unchanged from the pre-Carbon container.
 *
 * `OpportunityFilters` is now Carbon too (Task 5, the flow's final task): its `Category Filters`
 * panel heading is unchanged, and the `categoryFilters` id array it hands back via
 * `onFiltersChange` — this component's own prop wiring below — is unchanged in shape and timing,
 * only its internal markup (Carbon `Checkbox`/`Search`/`Tag` in place of Tailwind chips) moved.
 *
 * The `type`/count-by-type tab strip this component's own dead `hideHeader`/`hideTabs` props once
 * hinted at does NOT live here in the actual app: the only real mount site
 * (`components/OpportunityDashboard/OpportunityDashboard.tsx`) always renders this container in
 * "controlled" mode (`type` fixed, `disableQuerySync`), and OWNS the Indirect/Direct/Services +
 * Assigned Categories tab strip itself (`opportunityTabCards.ts`), because that strip must also
 * host the always-present "Assigned Categories" tab this container knows nothing about, and must
 * collapse to that one tab when every type count is 0 (`e2e/opportunities.spec.ts`'s pinned
 * shape). Those two dead, never-destructured props are dropped here rather than carried forward.
 */
const OpportunityDiscovery = ({
  type,
  disableQuerySync = false,
}: OpportunityDiscoveryProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isControlled = typeof type !== "undefined";
  const initialTypeFromQuery =
    (searchParams.get("type") as CategoryType) || CategoryType.INDIRECT_GOODS;

  const [internalType] = useState<CategoryType>(type ?? initialTypeFromQuery);
  const selectedType = isControlled ? (type as CategoryType) : internalType;

  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (disableQuerySync) return;
    setSearchParams({ type: selectedType });
  }, [disableQuerySync, selectedType, setSearchParams]);

  useEffect(() => {
    setPage(0);
  }, [selectedFilters, selectedType]);

  useEffect(() => {
    if (!isControlled) return;
    setSelectedFilters([]);
  }, [isControlled, selectedType]);

  const handleFiltersChange = (filters: string[]) => {
    setSelectedFilters(filters);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <div className="opportunity-discovery">
      <OpportunityFilters
        type={selectedType}
        selectedFilters={selectedFilters}
        onFiltersChange={handleFiltersChange}
      />

      <OpportunityTable
        type={selectedType}
        selectedFilters={selectedFilters}
        page={page}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default OpportunityDiscovery;
